import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { Waypoint } from 'react-waypoint';

import { CurationStatusBadge } from '@components/CurationStatusBadge';
import Icon from '@components/Icon/Icon';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SkeletonLoader from '@components/SkeletonLoader';
import Status from '@components/Status';

import * as ROUTES from '@constants/routes';

import { useConversion } from '@hooks/useConversion';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';
import { useThrottledLoader } from '@hooks/useThrottledLoader';

import { LabResultObservationRecord } from '@models/lab-result';

import { convertUnit, getUnit } from '@utils/conversion';
import { isBooleanValue } from '@utils/lab';

import type { GetLabResultResourcesQuery } from 'generated/diagnostic-report.generated';
import type { GetRecordInput } from 'generated/types';

import {
  OuterWrapper,
  ResultCard,
  Results,
  TypeName,
  Value,
} from './LabResultsSection.style';

interface LabResultsSectionProps {
  record: LabResultSectionRecord;
}

function LabResultsSection({
  record: {
    curationStatus,
    date: testDate,
    id,
    patient,
    recordSource,
    resultCount,
    type: testType,
    title,
    viewed,
  },
}: LabResultsSectionProps) {
  const {
    state: { conversion },
  } = useConversion();
  const {
    state: { isAdmin, isAuthenticated, isCurator, isPractitioner, isVitall },
  } = useSession();
  const [enabled, setEnabled] = React.useState(false);

  const {
    push,
    loading: queuedLoading,
    data,
  } = useThrottledLoader<GetLabResultResourcesQuery, GetRecordInput>();

  const patient_id = patient;
  const input: GetRecordInput = { patient_id, record_id: id };
  const sectionId = JSON.stringify(input);

  const isLoading = queuedLoading.includes(sectionId);
  const sectionData = data[sectionId];

  const { navigateRoute } = useNavigate();

  // Only query for results if lab section is in view for 0.5s or greater
  // Improves performance and reduces load on server when user scrolls quickly through list of labs
  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (enabled) {
        // Queue the API request, start loading
        push({
          patient_id,
          record_id: id,
        });
      }
    }, 0);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, id, patient_id]);

  const results = (sectionData?.getLabReport.resultResources ?? []).map(
    (result) => new LabResultObservationRecord(result)
  );

  const loading =
    isLoading || Boolean(enabled && !results.length && resultCount);

  const testKeys = results.map(({ unit, value, displayName, code }) => ({
    rKey: value,
    uKey: unit ?? '',
    displayName,
    type: code,
  }));

  const handleEnter = () => {
    setEnabled(true);
  };

  const handleLeave = () => {
    setEnabled(false);
  };

  function getValue(
    value: boolean | number | string | null | undefined,
    unit: string | null | undefined,
    type: string,
    table: string
  ) {
    const conversionTableName = type.split('|').at(1) ?? '';
    const conversionTable = conversion[conversionTableName];
    if (value === null || value === undefined) {
      return '';
    }
    if (isBooleanValue(value)) {
      return value ? 'Positive' : 'Negative';
    }
    if (!unit) {
      return value;
    }

    try {
      const numValue = parseFloat(value.toString());
      return (
        <>
          {convertUnit(
            numValue,
            unit,
            conversionTable ? 'CON' : 'SI',
            3,
            table,
            undefined,
            true
          )}{' '}
          {getUnit(unit, conversionTable ? 'CON' : 'SI', table)}
        </>
      );
    } catch {
      return (
        <>
          {value} {unit}
        </>
      );
    }
  }

  const handleNavigateReport = () => {
    if (isCurator || isPractitioner) {
      return navigateRoute(ROUTES.LAB_RESULTS_REPORT, { id });
    }
    return navigateRoute(ROUTES.LAB_RESULTS_REPORT, {
      category: testType,
      title,
    });
  };

  const handleNavigateDetail = (code: string, title: string) => {
    if (code === 'other' || !isAuthenticated) {
      return;
    }
    navigateRoute(ROUTES.LAB_RESULTS_REPORT_DETAIL, {
      category: testType,
      code,
      title,
    });
  };

  return (
    <OuterWrapper>
      <Stack
        direction="row"
        onClick={isAuthenticated ? handleNavigateReport : undefined}
        spacing={1.5}
        sx={{
          cursor: isAuthenticated ? 'pointer' : 'inherit',
        }}
      >
        {!viewed && isAuthenticated && <Status variant="blue" />}
        <Box>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 500, height: 15 }}
            variant="subtitle2"
          >
            {title}
          </Typography>
          <Typography color="grey.A700" variant="subtitle2">
            {testDate}
          </Typography>
        </Box>
        {isCurator || isAdmin ? (
          <CurationStatusBadge status={curationStatus} />
        ) : null}
        {isVitall || isPractitioner ? (
          <RecordSourcePill type={recordSource} />
        ) : null}
        <Icon color="grey.A700" name="chevron-right" size="lg" />
      </Stack>

      <Waypoint onEnter={handleEnter} onLeave={handleLeave}>
        <Results>
          <SkeletonLoader
            count={resultCount}
            loading={loading}
            width="100%"
            height={80}
          >
            {testKeys.map(({ rKey, uKey, displayName, type }, index) => (
              <ResultCard
                disabled={type === 'other'}
                key={`lab-result-card-${index}-${rKey}`}
                onClick={
                  isAuthenticated
                    ? () =>
                        handleNavigateDetail(type, displayName ?? 'Lab Results')
                    : undefined
                }
              >
                <TypeName>{displayName}</TypeName>
                <Value>{getValue(rKey, uKey, testType, type)}</Value>
              </ResultCard>
            ))}
          </SkeletonLoader>
        </Results>
      </Waypoint>
    </OuterWrapper>
  );
}

export default LabResultsSection;
